import Modal from '@components/Modal';
import { useModal } from '@components/Modal/ModalProvider';
import ResponseWaiting from '@components/ResponseWaiting/ResponseWaiting';
import useAuth from '@hooks/Auth/useAuth';
import { APIError } from '@models/index';
import { getErrorMessage, isAuthError } from '@api/api';
import {
  ComponentProps,
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ReactComponent as WarningIcon } from 'img/triangle-exclamation.svg';
import { ReactComponent as SuccessIcon } from 'img/failed_icon.svg';

export default function useModalActions() {
  const { refreshTokens } = useAuth();
  const { openModal, isModalOpen, updateModalContent, closeModal } = useModal();
  const loadingModalIdRef = useRef<string | null>(null);
  const loadingModalIdPropsRef = useRef<ComponentProps<
    typeof ResponseWaiting
  > | null>(null);
  const [waitingProgressMessage, setWaitingProgressMessage] =
    useState<string>();
  const loadingModalTimeoutRef = useRef<number | null>(null);

  const clearLoadingTimer = () => {
    if (loadingModalTimeoutRef.current !== null) {
      window.clearTimeout(loadingModalTimeoutRef.current);
      loadingModalTimeoutRef.current = null;
    }
  };

  const successAction = async (message: any, action?: () => void) => {
    const id = openModal(
      <Modal
        type="alert"
        close={() => closeModal(id)}
        onLeftClick={async () => {
          if (action) {
            await action();
          }
          closeModal(id);
        }}
        description={<Modal.TextDescription>{message}</Modal.TextDescription>}
      />,
    );
  };

  const errorDefaultAction = async (message?: string, action?: () => void) => {
    const id = openModal(
      <Modal
        type="alert"
        close={() => closeModal(id)}
        onLeftClick={async () => {
          if (action) {
            await action();
          }
          closeModal(id);
        }}
        description={<Modal.TextDescription>{message}</Modal.TextDescription>}
      />,
      false,
      undefined,
      true,
    );
  };

  const errorAction = async (
    error: APIError,
    message?: string,
    action?: () => void,
  ) => {
    if (isAuthError(error)) {
      refreshTokens();
    }

    const id = openModal(
      <Modal
        type="alert"
        close={() => closeModal(id)}
        onLeftClick={async () => {
          if (action) {
            await action();
          }
          closeModal(id);
        }}
        description={
          <Modal.TextDescription>
            {getErrorMessage(error, message)}
          </Modal.TextDescription>
        }
      />,
      false,
      undefined,
      true,
    );
  };

  const selectAction = async (
    message: string,
    actionRight: () => void,
    buttonLabel?: ComponentProps<typeof Modal>['buttonLabel'],
    className?: string,
    actionLeft?: () => void,
  ) => {
    const id = openModal(
      <Modal
        type="dialog"
        close={() => closeModal(id)}
        onLeftClick={async () => {
          await actionLeft?.();
          closeModal(id);
        }}
        onRightClick={async () => {
          await actionRight();
          closeModal(id);
        }}
        buttonLabel={buttonLabel}
        className={className}
        description={
          <Modal.TextDescription>
            {className === 'compact-warning-modal' ? (
              <span className="modal-warning-description">
                <WarningIcon className="modal-warning-icon" />
                <span>{message}</span>
              </span>
            ) : (
              message
            )}
          </Modal.TextDescription>
        }
      />,
      false,
      undefined,
      true,
    );
  };

  const selectIconAction = ({
    title,
    iconTitle,
    actionRight,
    options,
    actionArgs,
    buttonLabel,
  }: {
    title: string;
    iconTitle?: string;
    actionRight: (...args: any[]) => void;
    actionArgs?: any[];
    options?: {
      runActionOnClose?: boolean;
      Icon?: 'SuccessIcon' | 'WarningIcon';
    };
    buttonLabel?: ComponentProps<typeof Modal>['buttonLabel'];
  }) => {
    const { runActionOnClose = false, Icon = 'WarningIcon' } = options ?? {};

    const IconComponent = Icon === 'SuccessIcon' ? SuccessIcon : WarningIcon;
    let id: ReturnType<typeof openModal>;

    const handleClose = () => {
      if (runActionOnClose) {
        actionRight(...(actionArgs ?? []));
      }
      closeModal(id);
    };


    id = openModal(
      <Modal
        type="alert"
        close={handleClose}
        onLeftClick={() => {
          actionRight(...(actionArgs ?? []));
          closeModal(id);
        }}
        buttonLabel={buttonLabel}
        className="compact-warning-modal"
        description={
          <Modal.TextDescription>
            <span className="modal-warning-content">
              {title && (
                <span className="modal-warning-description">
                  <IconComponent className="modal-warning-icon" />
                  <span>{title}</span>
                </span>
              )}

              {iconTitle && (
                <span className="modal-warning-description">
                  {!title && <IconComponent className="modal-warning-icon" />}
                  <span>{iconTitle}</span>
                </span>
              )}
            </span>
          </Modal.TextDescription>
        }
      />,
      false,
      undefined,
      true,
    );
  };

  type WaitingActionOptions = {
    timeoutMs?: number;
    onTimeout?: () => void;
  };

  const waitingAction = (
    props: ComponentProps<typeof ResponseWaiting>,
    options?: WaitingActionOptions,
  ) => {
    if (isModalOpen && loadingModalIdRef.current) {
      closeModal(loadingModalIdRef.current);
    }
    clearLoadingTimer();

    loadingModalIdPropsRef.current = props;
    const id = openModal(
      <Modal
        type="page"
        close={() => closeModal(id)}
        description={
          <ResponseWaiting
            {...props}
            progressMessage={waitingProgressMessage}
          />
        }
      />,
      false,
      undefined,
      true,
    );
    loadingModalIdRef.current = id;

    if (options?.timeoutMs) {
      loadingModalTimeoutRef.current = window.setTimeout(() => {
        if (loadingModalIdRef.current === id) {
          closeLoadingModal();
          options.onTimeout?.();
        }
      }, options.timeoutMs);
    }
  };

  useEffect(() => {
    if (!loadingModalIdRef.current) return;
    updateModalContent(
      loadingModalIdRef.current,
      <Modal
        type="page"
        close={() => closeModal(loadingModalIdRef.current!)}
        description={
          <ResponseWaiting
            {...loadingModalIdPropsRef.current!}
            progressMessage={waitingProgressMessage}
          />
        }
      />,
    );
  }, [waitingProgressMessage]);
  const closeLoadingModal = () => {
    clearLoadingTimer();
    if (loadingModalIdRef.current !== null) {
      closeModal(loadingModalIdRef.current);
      loadingModalIdRef.current = null;
      setWaitingProgressMessage(undefined);
    }
  };

  return {
    isModalOpen,
    successAction,
    errorDefaultAction,
    errorAction,
    selectAction,
    waitingAction,
    closeLoadingModal,
    setWaitingProgressMessage,
    selectIconAction,
  };
}
